/**
 * The moored/docked transfer panel's model (M10 P5a8b, placeholder UI, D-052): for each of the player's other ships
 * together with the flown one, the lots that can go across (whole lots: a ware's tonnes, a good's units, one
 * component, one locker module), each way. Pure; the rules are `fleet-link/transfer.ts`'s (a button that the rule
 * refuses shows the refusal as its status line).
 */
import type { ModuleId, WareId } from '../../../data/m2Schemas'
import { GOOD_IDS } from '../../../career/parts'
import type { CargoState, Career } from '../../../career/types'
import { OWN_SHIP_ID, ownedShip } from '../../../fleet-link/hangar'
import { transferPartners, type ShipPlaces, type TransferLot } from '../../../fleet-link/transfer'

/** One button: move `lot` (or locker module `moduleId`) from ship `from` to ship `to`. */
export interface TransferRow {
  key: string
  label: string
  from: string
  to: string
  lot?: TransferLot
  moduleId?: ModuleId
}

export interface TransferPartnerView {
  id: string
  name: string
  /** Flown ship → this ship. */
  send: TransferRow[]
  /** This ship → the flown ship. */
  take: TransferRow[]
}

export interface TransferModel {
  partners: TransferPartnerView[]
}

function rowsOf(hold: CargoState | undefined, locker: readonly ModuleId[], from: string, to: string, pilot: boolean): TransferRow[] {
  const rows: TransferRow[] = []
  if (hold !== undefined) {
    for (const w of Object.keys(hold.wares) as WareId[]) {
      const t = hold.wares[w]
      if (t > 0) rows.push({ key: `${from}>${to}:w:${w}`, label: `${w} ${t.toFixed(1)} t`, from, to, lot: { kind: 'ware', ware: w, tonnes: t } })
    }
    for (const g of GOOD_IDS) {
      const q = hold.parts?.[g] ?? 0
      if (q > 0) rows.push({ key: `${from}>${to}:g:${g}`, label: `${g} ×${q}`, from, to, lot: { kind: 'good', good: g, qty: q } })
    }
    for (const c of hold.components) {
      if (pilot && (c.contractId !== undefined || c.sealed === true || c.story === true)) continue
      rows.push({ key: `${from}>${to}:c:${c.id}`, label: `${c.type} (${c.massT.toFixed(1)} t)`, from, to, lot: { kind: 'component', id: c.id } })
    }
  }
  for (const m of new Set(locker)) rows.push({ key: `${from}>${to}:m:${m}`, label: `module ${m}`, from, to, moduleId: m })
  return rows
}

/** The panel model, or null when no other ship of the player's is together with the flown one (nothing shows). */
export function transferModel(career: Career, places: ShipPlaces, nameOf: (fleetId: string) => string): TransferModel | null {
  const ids = transferPartners(career, places)
  if (ids.length === 0) return null
  const flown = career.flownShip ?? OWN_SHIP_ID
  const partners: TransferPartnerView[] = []
  for (const id of ids) {
    const e = ownedShip(career, id)
    if (e === null) continue
    partners.push({
      id,
      name: nameOf(id),
      send: rowsOf(career.cargo, career.ship.locker, flown, id, true),
      take: rowsOf(e.hold, e.locker, id, flown, false),
    })
  }
  return partners.length === 0 ? null : { partners }
}
